import { useContext, useMemo } from 'react';

import { Box, Card, Text, Toast, useToast } from '@zillow/constellation-10';
import { IconPrintFilled } from '@zillow/constellation-icons';

import { LoadingButton, LoadingImage, LoadingParagraph } from '@zg-rentals/loading-components';
import { RentalManagerPdfRendererWebApiHandler_getPropertySheetPdf } from '@zg-rentals/rental-manager-env';
import { usePdfFromRequester } from '@zg-rentals/pdf-utils';
import { logError } from '@zg-rentals/log-error';

import { PropertiesSelectors, RoutingSelectors, UserSelectors } from '../../selectors';
import { useSelector } from '../../selectors/useSelector';
import { LoadingMapContext } from '../../providers/LoadingMapContext/context';
import PrintPropertySheet from '../../images/post-rental-listing-light.svg';
import FindTenantsPageCSEvents from '../../model/ClickstreamEvent/FindTenantsPageCSEvents';

const ConnectedPropertySummarySheet = () => {
  const { enqueueToast } = useToast();

  const currentProperty = useSelector((state) => PropertiesSelectors.makeCurrentProperty(state));
  const user = useSelector((state) => UserSelectors.makeUser(state));
  const csEventParams = useMemo(() => ({ user, currentProperty }), [user, currentProperty]);
  const listingAlias = useSelector((state) => RoutingSelectors.makeListingId(state));

  const {
    loadingByComponent: {
      ConnectedApplications: isApplicationsLoading,
      InterestedRenters: isInterestedRentersLoading,
      ConnectedInquiries: isInquiriesLoading,
    },
  } = useContext(LoadingMapContext);

  const { getPdf, isLoading: isPdfLoading } = usePdfFromRequester({
    openInNewTab: true,
    requester: RentalManagerPdfRendererWebApiHandler_getPropertySheetPdf,
    onError: (err) => {
      logError({
        error: err,
        context: 'ConnectedPropertySummarySheet.getPropertySheetPdf',
      });
      enqueueToast(<Toast tone="critical">Could not generate PDF. Please try again later.</Toast>, {
        duration: 10_000,
      });
    },
  });

  const trackPrintButtonClick = () => {
    FindTenantsPageCSEvents.PrintPropertySheet(csEventParams);
  };

  const handlePrintButtonClick = () => {
    trackPrintButtonClick();
    getPdf({ listingAlias });
  };

  const isApplicationsTabLoading =
    isApplicationsLoading && (user.isEnterpriseCustomer ? isInquiriesLoading : isInterestedRentersLoading);

  return (
    <Box css={{ marginTop: { base: '200', md: 0 } }} data-testid="ConnectedPropertySummarySheet">
      <div className="ConnectedPropertySummarySheet">
        <Text
          asChild
          css={{
            textStyle: { base: 'body-bold', lg: 'heading-sm' },
            paddingTop: 'loose',
            paddingBottom: 'loose',
          }}
        >
          <h3>Invite more renters to apply</h3>
        </Text>
        <Card tone="neutral" elevated={false} css={{ padding: 'none', overflow: 'hidden' }}>
          <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)' }}>
            <Box css={{ gridColumnStart: '1', gridColumnEnd: { base: 'span 12', xl: 'span 4' } }}>
              <LoadingImage
                src={PrintPropertySheet}
                alt="print property sheet"
                isLoading={isApplicationsTabLoading}
                loadingWidth="100%"
                loadingHeight="230px"
              />
            </Box>
            <Box css={{ gridColumnStart: { base: '1', xl: '5' }, gridColumnEnd: { base: 'span 12', xl: 'span 8' } }}>
              <LoadingParagraph
                textStyle="body-bold"
                isLoading={isApplicationsTabLoading}
                loadingWidth="25%"
                css={{
                  margin: 'auto {spacing.400}',
                  marginTop: { base: '200', xl: '600' },
                }}
              >
                Print your property sheet
              </LoadingParagraph>
              <LoadingParagraph
                textStyle="body-sm"
                isLoading={isApplicationsTabLoading}
                loadingWidth="90%"
                css={{
                  margin: 'auto {spacing.400}',
                  marginTop: { base: '200', xl: 'default' },
                }}
              >
                Share your listing sheet with potential renters touring your property. Interested renters can apply with
                a simple scan of the QR code.
              </LoadingParagraph>
              <LoadingButton
                data-testid="ConnectedPropertySummarySheet-printPropertySheet"
                tone="brand"
                emphasis="outlined"
                icon={<IconPrintFilled />}
                loading={isPdfLoading}
                size="sm"
                onClick={handlePrintButtonClick}
                isLoading={isApplicationsTabLoading}
                loadingWidth="30%"
                css={{
                  margin: 'default',
                }}
                data-eventid={FindTenantsPageCSEvents.PrintPropertySheet.data_event_id}
              >
                Print listing sheet
              </LoadingButton>
            </Box>
          </Box>
        </Card>
      </div>
    </Box>
  );
};

export { ConnectedPropertySummarySheet };
export default ConnectedPropertySummarySheet;
